import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Company, DocusignWaiver } from '../../interfaces/users.interface';

import Layout from '../Layout/Layout';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ApartmentIcon from '@mui/icons-material/Apartment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AppsIcon from '@mui/icons-material/Apps';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SubNavColumn, { SubNavItem } from '../Navigation/SubNavColumn';
import { COMPANY_INNER_TAB_KEYS, visibleCompanyInnerTabKeys, visibleStandaloneSectionIds } from './companyProfileNav';
import { SectionPagerProvider, SectionPagerState } from './sectionPager';
import { PaginationBar } from './companyTabUi';

import ServicesTab from '../Directory/Services/ServicesTab';
import {
  updateDocusignWaiver,
  sendDocusignWaiver,
  resendDocusignWaiver,
  updateCompanyLogo,
  updateCompanyBoolean, updateCompanyAsPaid,
} from '../../services/company';
import { createWaiver, fetchWaiver } from '../../services/docusignWaivers';
import canAdminCompany from '../../services/adminUtils';
import SitesTab from '../Directory/Sites/SitesAccordion/SitesTab';
import CompanyProfileHeader from './CompanyProfileHeader';
import AdminTab from './AdminTab';
import { useCompanyDetail } from './useCompanyDetail';
import CompanyApplicationsTab from '../Directory/Applications/CompanyApplicationsAccordion/CompanyApplicationsTab';
import CompanyCertificationsTab from '../Directory/Certifications/CompanyCertificationsTab';
import CompanyDocumentsTab from '../Directory/Applications/Documents/CompanyDocumentsTab';
import { fetchPublishedQuestionnaire, resetSurveys } from '../../app/store/surveySlice';
import { Questionnaire } from '../../interfaces/questionnaire.interface';
import CompanyForm from '../Companies/Forms/CompanyForm';
import CompanySubsidiariesTab from '../Directory/Sibsidiaries/CompanySubsidiariesTab';
import UsersTab from '../Directory/Profile/UsersTab';
import PendingUsersTab from '../Directory/Profile/PendingUsersTab';
import AssessmentsTab from '../Directory/Assessments/AssessmentsTab';
import { createSurvey, fetchSurvey } from '../../app/store/surveySlice';
import { BestPracticeStatus, Survey } from '../../interfaces/survey.interface';
import StandardDialog from '../Modals/StandardDialog';
import FileUploaderComp from '../Forms/FileUpload';
import { fetchAssessmentAuditLog, resetAssessments } from '../../app/store/assessmentSlice';
import { AssessmentSurvey } from '../../interfaces/assessment.interface';
import moment from 'moment';
import { fetchAllApplicationTypes, fetchAllServiceCategories } from '../../app/store/serviceCategories';
import { fetchAllCompaniesStore } from '../../app/store/companyList';
import { fetchAllWorkflows } from '../../app/store/workflowSlice';
import { QuestionnaireTypes } from '../../services/questionnaires';
import CompanyLegalContactForm from '../Companies/Forms/CompanyLegalContactForm';
import { USER_TYPES } from '../../services/user';
import { COMPANY_TYPES } from '../../services/vendor';
import UpcomingArchiveWarning from '../../components/Alerts/UpcomingArchiveWarning';
import { TPN_BEST_PRACTICE_5_3_1 } from '../../services/workflow';
import { CompanyProfileV1, VendorProfileV1 } from './ProfileV1';
import { useProfileVersion } from './profileVersion';

// Full-bleed sub-nav shell layout (matches the prototype: sub-nav + content column,
// content scrolls, pager pinned as a footer). `filled` = routed/full-height.
const ProfileShell = styled('div', { shouldForwardProp: (prop) => prop !== 'filled' })<{ filled?: boolean }>(
  ({ filled }) => ({ display: 'flex', minHeight: 0, ...(filled ? { flex: 1 } : { height: '70vh' }) }),
);
const ContentColumn = styled('div')({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 });
const ScrollArea = styled('div')({ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', padding: 24 });
// White frame around a section's content on the v2 canvas (matches the v1 card).
// Section content self-pads (each tab's own Stack), so the card adds no padding.
const SectionCard = styled('div')(({ theme }) => ({ background: '#fff', borderRadius: 4, boxShadow: theme.shadows[1], overflow: 'hidden' }));

const ColumnHeaderRoot = styled('div')({ display: 'flex', gap: 12, alignItems: 'center', padding: '16px 16px 14px' });
const ColumnAvatar = styled('div')({ width: 44, height: 44, borderRadius: 8, background: 'linear-gradient(135deg,#1c2a42,#3868B9)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15 });
const ColumnNameWrap = styled('div')({ minWidth: 0 });
const ColumnName = styled('div')({ fontWeight: 700, fontSize: 14, color: '#1c2a42', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
const ColumnSubtitle = styled('div')({ fontSize: 12, color: '#8a94a3' });

const SectionHeaderRow = styled('div')({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 });
const SectionTitle = styled('h2')({ margin: 0, fontSize: 20, fontWeight: 300, color: '#1c2a42' });
const CompanyTabsRow = styled('div')(({ theme }) => ({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}`, marginTop: 16 }));
const HeaderActions = styled('div')({ display: 'flex', gap: 8 });

const ActionButton = styled(Button)({ textTransform: 'none', color: '#1976d2', fontWeight: 600, paddingLeft: 16, paddingRight: 16, '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' } });
const InnerTabs = styled(Tabs)({
  borderBottom: 'none',
  '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', color: '#5a6472', minWidth: 'auto', padding: '12px 20px', '&.Mui-selected': { color: '#1976d2' } },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function CompanyProfileV2(props: { company: Company; routed?: boolean }) {
  const navigate = useNavigate();
  const routeParams = useParams();
  const { applicationTypes, fetchingApplicationTypes, serviceCategories } = useAppSelector(state => state.serviceCategories);
  const { companies, fetchingCompanies } = useAppSelector(state => state.companyList);
  const { workflows } = useAppSelector(state => state.workflow);
  const { user } = useAppSelector(state => state.user);
  const { results: assessments, assessmentLocalStorageItems } = useAppSelector(state => state.assessments);
  const dispatch = useAppDispatch();
  const {
    surveys,
    survey,
    mpa_best_practice: mpaBestPractice,
    site_baseline: siteBaseline,
    application_baseline: applicationBaseline,
    assessor_onboarding: assessorOnboarding,
    content_owner_onboarding: contentOwnerOnboarding,
    vendor_onboaring: vendorOnboarding,
  } = useAppSelector(state => state.surveys);
  const { results: questionnaires } = useAppSelector(state => state.questionnaires);
  const [company, setCompany] = useState(props.company);
  const [headerReloadToken, setHeaderReloadToken] = useState(0);
  const { detail: companyDetail, loading: companyDetailLoading, refetch: refetchCompanyDetail } = useCompanyDetail(props.company.id, headerReloadToken);
  const refreshHeader = useCallback(() => {
    setHeaderReloadToken((token) => token + 1);
    refetchCompanyDetail();
  }, [refetchCompanyDetail]);
  const [waiverSent, setWaiverSent] = useState<undefined | string>(undefined);
  const [logoFormOpen, setLogoFormOpen] = useState(false);
  const [createSiteOpen, setCreateSiteOpen] = useState(false);
  const [addApplicationOpen, setAddApplicationOpen] = useState(false);
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false);
  const [uploadCertificationOpen, setUploadCertificationOpen] = useState(false);
  const [editServicesOpen, setEditServicesOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const routed = props.routed ?? true;
  const [internalSection, setInternalSection] = useState('company');
  const [innerTab, setInnerTab] = useState(() => {
    const seg = routed ? routeParams.profileSection : undefined;
    return seg && (COMPANY_INNER_TAB_KEYS as readonly string[]).includes(seg) ? seg : 'services';
  });
  // Active section registers its pager here so the shell renders one pinned footer.
  const [sectionPager, setSectionPager] = useState<SectionPagerState | null>(null);
  const handleLogoFormOpen = () => setLogoFormOpen(true);
  const handleLogoFormClose = () => setLogoFormOpen(false);
  const [onboardingSurvey, setOnboardingSurvey] = useState<Survey | undefined | boolean>(undefined);

  const logoFileChangeHandler = (file?: any) => {
    const actualFile = file ? file[0].file : undefined;
    if (!actualFile && !company.logo) return;
    updateCompanyLogo(company.id!, actualFile ? actualFile : '').then((companyResponse: Company) => {
      setCompany(companyResponse);
      refreshHeader();
      handleLogoFormClose();
    });
  };

  const upcomingArchivedQuestionnaires = useMemo(() => {
    if (!workflows?.length || !questionnaires?.length) return [];

    const now = moment();

    const filtered = questionnaires.filter(({ archiveDate, version, type }) => {
      if (!archiveDate || !version || type !== QuestionnaireTypes.MPA_BEST_PRACTICE) return false;

      const daysUntilArchive = moment(archiveDate).diff(now, 'days');
      if (daysUntilArchive < 0 || daysUntilArchive > 14) return false;

      return workflows.some((workflow) =>
        workflow.workflowType.includes(workflow.workflowType === TPN_BEST_PRACTICE_5_3_1 ? version : version.replace('.0', '')),
      );
    });

    return [...filtered].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  }, [workflows, questionnaires]);

  const patchCompany = useCallback((patch: Company) => {
    setCompany(patch);
  }, []);


  const getOnboardingId = () => {
    if (!company) return -1;
    if (company.type === COMPANY_TYPES.VENDOR) return vendorOnboarding;
    if (company.type === COMPANY_TYPES.CONTENT_OWNER) return contentOwnerOnboarding;
    if (company.type === COMPANY_TYPES.ASSESSOR) return assessorOnboarding;
    return -1;
  };

  useEffect(() => {
    if (!assessments?.length || !assessmentLocalStorageItems) return;

    const assessmentSurveyIds:number[] = [];
    assessments.forEach(ass => {
      (ass.surveys as AssessmentSurvey[]).forEach(assSur => {
        if (!assSur.reportIssueDate && assSur.id) assessmentSurveyIds.push(assSur.id);
      });
    });
    if (!assessmentSurveyIds.length) return;

    const assessmentSurveyFetch = () => {
      assessmentSurveyIds.forEach(id => {
        const assLoc = assessmentLocalStorageItems.find(assLocI => assLocI.assessmentSurvey === id);
        if (!assLoc) return;
        // TPE-2919: fetch window is decoupled from the last-view date (see fetchAssessmentAuditLog).
        dispatch(fetchAssessmentAuditLog({ assessmentSurvey: id }));
      });
    };

    assessmentSurveyFetch();
    const assessmentActionInterval = setInterval(assessmentSurveyFetch, 60000);

    return (() => {
      clearInterval(assessmentActionInterval);
    });
  }, [assessments]);

  useEffect(() => {
    if (!mpaBestPractice) dispatch(fetchPublishedQuestionnaire(QuestionnaireTypes.MPA_BEST_PRACTICE));
    if (!siteBaseline) dispatch(fetchPublishedQuestionnaire(QuestionnaireTypes.SITE_BASELINE));
    if (!applicationBaseline) dispatch(fetchPublishedQuestionnaire(QuestionnaireTypes.APPLICATION_BASELINE));
    if (company.type === COMPANY_TYPES.ASSESSOR && !assessorOnboarding) dispatch(fetchPublishedQuestionnaire('assessor_onboarding'));
    if (company.type === COMPANY_TYPES.CONTENT_OWNER && !contentOwnerOnboarding) dispatch(fetchPublishedQuestionnaire('content_owner_onboarding'));
    if (company.type === COMPANY_TYPES.VENDOR && !vendorOnboarding) dispatch(fetchPublishedQuestionnaire('vendor_onboaring'));

    if (company.type !== COMPANY_TYPES.VENDOR || user?.type === USER_TYPES.VENDOR_ADMIN) return;

    const SPLocalStorageJSON = localStorage.getItem('SPLast10');
    const SPLocalStorage = SPLocalStorageJSON === null ? [] : JSON.parse(SPLocalStorageJSON).filter((sp:any) => sp.id != company.id);
    if (SPLocalStorage.length === 10) SPLocalStorage.pop();
    SPLocalStorage.unshift({ id: company.id, name: company.name });
    localStorage.setItem('SPLast10', JSON.stringify(SPLocalStorage));

    if (!applicationTypes.length && !fetchingApplicationTypes) dispatch(fetchAllApplicationTypes());
    if (!serviceCategories.length) dispatch(fetchAllServiceCategories());
    if (!companies.length && !fetchingCompanies) dispatch(fetchAllCompaniesStore());
  }, []);

  useEffect(() => {
    if (!company ||
      !surveys ||
      (company.type === COMPANY_TYPES.ASSESSOR && !assessorOnboarding) ||
      (company.type === COMPANY_TYPES.CONTENT_OWNER && !contentOwnerOnboarding) ||
      (company.type === COMPANY_TYPES.VENDOR && !vendorOnboarding)
    ) return;
    const onBoardingId = getOnboardingId();
    const onboardingSurveys = surveys.filter((cursur: Survey) => (cursur.company as Company).id === company.id && (cursur.questionnaire as Questionnaire).id === onBoardingId).sort((a, b) => b.id - a.id);
    setOnboardingSurvey(onboardingSurveys && onboardingSurveys.length > 0 ? onboardingSurveys[0] : false);
    return (()=> {
      setOnboardingSurvey(undefined);
    });
  }, [ assessorOnboarding, contentOwnerOnboarding, vendorOnboarding, surveys, survey ]);

  useEffect(() => {
    if (!props.company.id) return;
    dispatch(resetSurveys());
    return (()=> {
      dispatch(resetAssessments());
    });
  }, [props.company]);

  // The header, alerts, tab labels and every tab now render from the
  // /company-detail/ endpoints, so the parent just keeps the company scalars
  // (id, type, waiver, isPaid, primaryContactUser, …) fresh from the detail
  // payload. The heavy full company is never fetched on this page.
  useEffect(() => {
    if (companyDetail) setCompany((prev) => ({ ...prev, ...companyDetail } as Company));
  }, [companyDetail]);

  useEffect(() => {
    if (!user || !company || company.isPaid) return;

    if (user?.companies.length && user.companies[0].id === company.id && user.companies[0].isPaid) setCompany({ ...company, isPaid: user.companies[0].isPaid });
  }, [user]);

  const markCompanyAsPaid = () => {
    updateCompanyAsPaid(company).then((companyResponse: Company) => { setCompany(companyResponse); refreshHeader(); });
  };

  const resetCompany = () => {
    if (!company.id) return;
    // Waiver/billing fields live on the detail payload; refresh that instead of
    // re-fetching the full company.
    refreshHeader();
  };

  const sendDocusignWaiverToCompany = () => {
    if (!company.id || !company.primaryContactUser?.email) return;
    sendDocusignWaiver(company).then(() => { resetCompany(); setWaiverSent(moment().format('h:mm')); });
  };

  const resendDocusignWaiverToCompany = () => {
    if (!company.id || !company.primaryContactUser?.email || !company.waiver?.id) return;
    resendDocusignWaiver(company).then(() => { resetCompany(); setWaiverSent(moment().format('h:mm')); });
  };

  const markWaiverComplete = (waiver: DocusignWaiver | undefined) => {
    if (!waiver?.id) {
      createWaiver({
        envelopeCompleted: true,
        recipientCompleted: true,
        waiverCompletedAt: new Date(),
      }).then(waiverResponse => {
        setCompany({ ...company, waiver: waiverResponse });
        updateCompanyBoolean({
          id: company.id,
          waivers: [...(company.waivers || []), waiverResponse.id],
        });
      });
    } else {
      waiver.envelopeCompleted = true;
      waiver.recipientCompleted = true;
      waiver.waiverCompletedAt = waiver.waiverCompletedAt ? waiver.waiverCompletedAt : new Date();
      waiver.userSignWaiverAt = waiver.userSignWaiverAt ? waiver.userSignWaiverAt : new Date();
      updateDocusignWaiver(waiver).then((waiverResponse) => {
        company.waiver = waiverResponse;
        setCompany({ ...company });
        updateCompanyBoolean({
          id: company.id,
          waivers: [...(company.waivers || []), waiverResponse.id],
        });
      });
    }
  };

  // Prefer the viewer-permission flag from /company-detail/; fall back to the
  // company-derived check until it loads (or for callers without it).
  const userCanAdminCompany: boolean = canAdminCompany(user, company);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [showEditCompanyDetailModal, setShowEditCompanyDetailModal] = useState<boolean>(false);
  const [showEditCompanyLegalContactModal, setShowEditCompanyLegalContactModal] = useState<boolean>(false);
  const handleSetPrimaryContact = (updatedCompany: Company) => {
    setCompany(updatedCompany);
    refreshHeader();
  };
  const handleEditCompanyDetail = (updatedCompany: Company) => {
    setCompany(updatedCompany);
    refreshHeader();
    setShowEditCompanyDetailModal(false);
  };
  const updateCompanyLegalContactCallback = (updatedCompany: Company) => {
    setCompany(updatedCompany);
    refreshHeader();
    setShowEditCompanyLegalContactModal(false);
  };
  const linkHandler = useCallback(() => navigate(`/questionnaire/${survey?.id}/`), [navigate, survey]);

  const onboardingComplete = () => {
    let shouldShow = false;
    if (!onboardingSurvey || typeof onboardingSurvey === 'boolean') return shouldShow;

    shouldShow = (onboardingSurvey.bestPracticeStatuses as BestPracticeStatus[])?.length > 0 ? false : true;
    (onboardingSurvey.bestPracticeStatuses as BestPracticeStatus[])?.forEach((bestPracticeStatus) => {
      if (bestPracticeStatus.questionsAnsweredCount !== bestPracticeStatus.visibleQuestionsCount) shouldShow = true;
    });
    return shouldShow;
  };

  useEffect(() => {
    if (!survey) return;
    if ((survey.questionnaire as Questionnaire).id === getOnboardingId() && !onboardingComplete()) linkHandler();
  }, [survey]);

  // fetch all workflows
  useEffect(() => {
    if (company?.id) {
      const key =  company.type === COMPANY_TYPES.ASSESSOR ? COMPANY_TYPES.ASSESSOR : 'service_provider';
      dispatch(fetchAllWorkflows({ [key]: company.id, ordering: '-created_at' }));
    }
  }, [company.id]);

  const launchOnboardingSurvey = () => {
    if (!onboardingSurvey) {
      const surId = getOnboardingId();
      if (!company || !company.id || surId === -1 || !surId) return;
      dispatch(createSurvey({ company: company.id, questionnaire: surId }));
    } else {
      dispatch(fetchSurvey((onboardingSurvey as Survey).id as number));
    }
  };

  useEffect(() => {
    const fetchAndUpdateWaiverStatus = () => {
      if (!company?.waiver?.id) {
        return;
      }
      fetchWaiver(company?.waiver.id).then((waiverResponse) => {
        if (waiverResponse.envelopeCompleted || waiverResponse.recipientCompleted) {
          setCompany(prevCompany => ({
            ...prevCompany,
            waiver: waiverResponse,
          }));
        }
      });
    };

    // Set an interval for continuous polling.
    const waiverPollingInterval = setInterval(fetchAndUpdateWaiverStatus, 30000);

    // Fetch immediately before starting the interval.
    fetchAndUpdateWaiverStatus();

    return () => {
      clearInterval(waiverPollingInterval);
    };
  }, [company?.waiver?.id, company?.waiver?.envelopeCompleted, setCompany]);

  const headerAlerts = (
    <>
      {(userCanAdminCompany || user?.type === USER_TYPES.TPN_ADMIN) &&
        <Stack spacing={1} sx={{ mb: 2 }}>
          {(onboardingSurvey === false || (onboardingSurvey && onboardingSurvey !== true && onboardingComplete())) &&
            <Alert severity="info" action={
              <Button onClick={ () => launchOnboardingSurvey() } sx={{ whiteSpace: 'nowrap' }}>Launch Questionnaire</Button>
            }>Please Complete the onboarding Questionnaire.</Alert>
          }

           <UpcomingArchiveWarning questionnaires={upcomingArchivedQuestionnaires} />

          {!company.waiver?.envelopeCompleted && company?.waiver?.recipientCompleted && (company.type === COMPANY_TYPES.VENDOR || user?.type === USER_TYPES.TPN_ADMIN) && <Alert severity="info" action={<>
            { user?.type === USER_TYPES.TPN_ADMIN &&
                <Button sx={{ whiteSpace: 'nowrap' }} onClick={() => markWaiverComplete(company.waiver)}>Mark as complete</Button>
              }
          </>}>TPN is reviewing your membership agreement.</Alert>}
          { !company.waiver?.recipientCompleted && (company.type === COMPANY_TYPES.VENDOR || user?.type === USER_TYPES.TPN_ADMIN) &&
            <Alert severity="warning" action={<>
              {company?.primaryContactUser?.email && !company?.waiver && <Button sx={{ whiteSpace: 'nowrap' }} onClick={() => sendDocusignWaiverToCompany()} color="warning">Send Membership Agreement</Button>}
              {company?.primaryContactUser?.email && company?.waiver && !company?.waiver?.recipientCompleted && <Button sx={{ whiteSpace: 'nowrap' }} onClick={() => resendDocusignWaiverToCompany()} color="warning">Resend Membership Agreement</Button>}
              { user?.type === USER_TYPES.TPN_ADMIN &&
                <Button sx={{ whiteSpace: 'nowrap' }} onClick={() => markWaiverComplete(company.waiver)} color="warning">Mark as complete</Button>
              }
            </>}>You need to sign your membership agreement. Please note the execution date of the agreement is your membership start date and payment will be due.{!company?.primaryContactUser?.email && ' A company primary contact is required.'}{waiverSent && ` Your Waiver was sent at ${waiverSent}.`}</Alert>
          }
          { !company.isPaid && !company.hasPreviouslyPaid &&
            <Alert severity="error" action={<>
              { user?.type === USER_TYPES.TPN_ADMIN &&
                <Button sx={{ whiteSpace: 'nowrap' }} onClick={() => markCompanyAsPaid()} color="error">Mark as paid</Button>
              }
            </>}>Your membership fee has not yet been paid. Invoices are distributed early each week after the membership agreement has been fully executed. Please note your profile will not be visible in the TPN+ registry until your membership fee is paid.</Alert>
          }
          { !company.isPaid && company.hasPreviouslyPaid &&
            <Alert severity="error" action={<>
              { user?.type === USER_TYPES.TPN_ADMIN &&
                <Button sx={{ whiteSpace: 'nowrap' }} onClick={() => markCompanyAsPaid()} color="error">Mark as paid</Button>
              }
            </>}>Your membership fee has not been received and we regret to inform you that your TPN+ account will be deactivated. This means that you will still have access to your TPN+ profile and all information, however you will no longer be visible in the TPN+ Company registry and Content Owners will not be able to see your security status until payment is received.</Alert>
          }
        </Stack>
      }
    </>
  );

  return (
    <>
      <StandardDialog title={`${company.logo ? 'Update' : 'Add'} Company Logo`} handleClose={handleLogoFormClose} isOpen={logoFormOpen}>
        <Stack spacing={2}>
          <Grid item xs={12} mt={2} mb={2}>
            <FileUploaderComp callback={(file: File) => logoFileChangeHandler(file)} multiple={false} hideFileList={true} />
          </Grid>
        </Stack>
      </StandardDialog>
      <StandardDialog title='Edit Company' handleClose={() => setShowEditCompanyDetailModal(false)} isOpen={showEditCompanyDetailModal}>
        <CompanyForm successCallback={handleEditCompanyDetail} company={company} assignUserToEmployees={false} submitButtonText='Update Company' primaryContactCallback={handleSetPrimaryContact} />
      </StandardDialog>
      <StandardDialog title={`${company?.companyLegalContacts?.length ? 'Update' : 'Add'} Legal Contact`} handleClose={() => setShowEditCompanyLegalContactModal(false)} isOpen={showEditCompanyLegalContactModal}>
        <CompanyLegalContactForm company={company} companyCallBack={updateCompanyLegalContactCallback} />
      </StandardDialog>
      {(() => {
        const addButton = (label: string, onClick: () => void) => (
          <ActionButton variant='text' size='small' startIcon={<AddCircleOutlineIcon />} onClick={onClick}>{label}</ActionButton>
        );

        const pendingUserCount = companyDetail?.pendingUserCount ?? company.pendingEmployees?.length ?? 0;

        // Inner tabs of the Company section — content about the company itself.
        // These live under the base profile path; they do not get their own routes.
        // Visibility is gated by visibleCompanyInnerTabKeys (single source of truth).
        const innerTabVisible = visibleCompanyInnerTabKeys({ companyType: company.type, userType: user?.type, userCanAdminCompany, pendingUserCount });
        const innerTabs = ([
          {
            key: 'services',
            label: 'Services',
            content: <ServicesTab company={company} companyCallback={patchCompany} userCanAdminCompany={userCanAdminCompany} editServicesOpen={editServicesOpen} onEditServicesClose={() => setEditServicesOpen(false)} />,
            addButton: userCanAdminCompany ? addButton('Edit Services', () => setEditServicesOpen(true)) : undefined,
          },
          {
            key: 'subsidiaries',
            label: 'Subsidiaries',
            content: <CompanySubsidiariesTab company={company} companyCallback={patchCompany} />,
          },
          {
            key: 'users',
            label: 'Users',
            content: <UsersTab company={company} companyCallback={patchCompany} createUserOpen={createUserOpen} onCreateUserClose={() => setCreateUserOpen(false)} userCanAdminCompany={userCanAdminCompany} userCount={companyDetail?.userCount} onMembershipChange={refreshHeader} />,
            addButton: userCanAdminCompany ? addButton('Add User', () => setCreateUserOpen(true)) : undefined,
          },
          {
            key: 'pending-users',
            label: `Pending Users (${pendingUserCount})`,
            content: <PendingUsersTab company={company} companyCallback={patchCompany} onMembershipChange={refreshHeader} />,
          },
          {
            key: 'admin',
            label: 'Admin',
            content: <AdminTab companyId={company.id!} detail={companyDetail} userType={user?.type} onEditCompany={() => setShowEditCompanyDetailModal(true)} onEditLegalContact={() => setShowEditCompanyLegalContactModal(true)} />,
          },
        ] as { key: string; label: string; content: React.ReactNode; addButton?: React.ReactNode }[]).filter((t) => innerTabVisible.includes(t.key));

        // Standalone sub-nav sections — each its own screen and deep-linkable route.
        // Visibility (e.g. Assessments hidden for Content Owner) via visibleStandaloneSectionIds.
        const standaloneVisible = visibleStandaloneSectionIds(company.type, user?.type);
        const standaloneSections = ([
          {
            id: 'sites', label: 'Sites', icon: LocationOnIcon,
            count: companyDetail?.siteCount ?? company.locations?.length ?? 0,
            content: <SitesTab company={company} companyCallback={patchCompany} userCanAdminCompany={userCanAdminCompany} createSiteOpen={createSiteOpen} onCreateSiteOpen={() => setCreateSiteOpen(true)} onCreateSiteClose={() => setCreateSiteOpen(false)} />,
            addButton: userCanAdminCompany ? addButton('Add Site', () => setCreateSiteOpen(true)) : undefined,
          },
          {
            id: 'applications', label: 'Applications', icon: AppsIcon,
            count: companyDetail?.applicationCount ?? company.companyApplications?.length ?? 0,
            content: <CompanyApplicationsTab company={company} companyCallback={patchCompany} userCanAdminCompany={userCanAdminCompany} addApplicationOpen={addApplicationOpen} onAddApplicationClose={() => setAddApplicationOpen(false)} />,
            addButton: userCanAdminCompany ? addButton('Add App', () => setAddApplicationOpen(true)) : undefined,
          },
          {
            id: 'certifications', label: 'Certifications', icon: WorkspacePremiumIcon,
            count: companyDetail?.certificationCount ?? company.companyCertifications?.length ?? 0,
            content: <CompanyCertificationsTab company={company} companyCallback={patchCompany} userCanAdminCompany={userCanAdminCompany} uploadDocumentOpen={uploadCertificationOpen} onUploadDocumentClose={() => setUploadCertificationOpen(false)} />,
            addButton: userCanAdminCompany ? addButton('Add Certification', () => setUploadCertificationOpen(true)) : undefined,
          },
          {
            id: 'assessments', label: 'Assessments', icon: AssignmentIcon,
            content: <AssessmentsTab company={company} userCanAdminCompany={userCanAdminCompany} assessmentModalOpen={assessmentModalOpen} onAssessmentModalClose={() => setAssessmentModalOpen(false)} />,
            addButton: (user?.type === USER_TYPES.VENDOR_ADMIN && userCanAdminCompany) ? addButton('New Assessment', () => setAssessmentModalOpen(true)) : undefined,
          },
          {
            id: 'documents', label: 'Documents', icon: DescriptionIcon,
            count: companyDetail?.documentCount ?? company.companyDocuments?.length ?? 0,
            content: <CompanyDocumentsTab company={company} companyCallback={patchCompany} userCanAdminCompany={userCanAdminCompany} uploadOpen={uploadDocumentOpen} onUploadClose={() => setUploadDocumentOpen(false)} />,
            addButton: userCanAdminCompany ? addButton('Add Document', () => setUploadDocumentOpen(true)) : undefined,
          },
        ] as { id: string; label: string; icon: React.ElementType; count?: number; content: React.ReactNode; addButton?: React.ReactNode }[]).filter((s) => standaloneVisible.includes(s.id));

        const standaloneIds = standaloneSections.map((s) => s.id);
        const rawSection = routed ? (routeParams.profileSection ?? 'company') : internalSection;
        const activeSection = standaloneIds.includes(rawSection) ? rawSection : 'company';

        const base = routed ? (routeParams.companyId ? `/registry/${routeParams.companyId}` : '/profile') : undefined;
        const sectionTo = (id: string) => (base ? (id === 'company' ? base : `${base}/${id}`) : undefined);

        const subNavItems: SubNavItem[] = [
          { id: 'company', label: 'Company', icon: ApartmentIcon, to: sectionTo('company') },
          ...standaloneSections.map((s) => ({ id: s.id, label: s.label, icon: s.icon, count: s.count, to: sectionTo(s.id) })),
        ];

        const companyInitials = (company.name || '').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
        const columnHeader = (
          <ColumnHeaderRoot>
            <ColumnAvatar>{companyInitials}</ColumnAvatar>
            <ColumnNameWrap>
              <ColumnName>{company.name}</ColumnName>
              <ColumnSubtitle>Company Profile</ColumnSubtitle>
            </ColumnNameWrap>
          </ColumnHeaderRoot>
        );

        const activeStandalone = standaloneSections.find((s) => s.id === activeSection);
        const innerIndex = Math.max(0, innerTabs.findIndex((t) => t.key === innerTab));
        const currentInner = innerTabs[innerIndex];

        return (
          <SectionPagerProvider value={setSectionPager}>
            <ProfileShell filled={routed}>
              <SubNavColumn
                header={columnHeader}
                items={subNavItems}
                activeId={activeSection}
                ariaLabel='Company sections'
                onSelect={routed ? undefined : setInternalSection}
              />
              <ContentColumn>
                <ScrollArea>
                  {headerAlerts}
                  {activeSection === 'company' ? (
                    <>
                      <CompanyProfileHeader
                        userType={user?.type}
                        userCanAdminCompany={userCanAdminCompany}
                        actionsMenuOpen={open}
                        detail={companyDetail}
                        loading={companyDetailLoading}
                        onAddLogoClick={handleLogoFormOpen}
                        onActionsMenuClick={handleClick}
                      />
                      {innerTabs.length > 0 && (
                        <SectionCard>
                          <CompanyTabsRow>
                            <InnerTabs
                              value={innerIndex}
                              onChange={(e, newValue) => setInnerTab(innerTabs[newValue].key)}
                              aria-label='company profile tabs'
                            >
                              {innerTabs.map((tab) => (<Tab key={tab.key} label={tab.label} />))}
                            </InnerTabs>
                            <HeaderActions>{currentInner?.addButton}</HeaderActions>
                          </CompanyTabsRow>
                          {innerTabs.map((tab, idx) => (
                            <TabPanel key={tab.key} value={innerIndex} index={idx}>{tab.content}</TabPanel>
                          ))}
                        </SectionCard>
                      )}
                    </>
                  ) : activeStandalone ? (
                    <>
                      <SectionHeaderRow>
                        <SectionTitle>{activeStandalone.label}</SectionTitle>
                        <HeaderActions>{activeStandalone.addButton}</HeaderActions>
                      </SectionHeaderRow>
                      <SectionCard>{activeStandalone.content}</SectionCard>
                    </>
                  ) : null}
                </ScrollArea>
                {sectionPager && (
                  <PaginationBar
                    variant='pinned'
                    count={sectionPager.count}
                    page={sectionPager.page}
                    pageSize={sectionPager.pageSize}
                    onPage={sectionPager.onPage}
                    onPageSize={sectionPager.onPageSize}
                  />
                )}
              </ContentColumn>
            </ProfileShell>
          </SectionPagerProvider>
        );
      })()}


      {userCanAdminCompany && <>
        <Menu
          anchorEl={anchorEl}
          id="company-actions-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {onboardingSurvey && <MenuItem onClick={ () => launchOnboardingSurvey() }>
            <ListItemIcon>
              <DescriptionIcon />
            </ListItemIcon>
            Launch Onboarding
          </MenuItem>}
            <MenuItem onClick={() => { setShowEditCompanyDetailModal(true); }}>
              <ListItemIcon>
                <BusinessOutlinedIcon />
              </ListItemIcon>
              Edit Company
            </MenuItem>
          {company.logo &&
            <MenuItem onClick={() => { handleLogoFormOpen(); }}>
              <ListItemIcon>
                <ImageOutlinedIcon />
              </ListItemIcon>
              Edit Company Logo
            </MenuItem>
          }
          {company.logo &&
            <MenuItem onClick={() => { logoFileChangeHandler(); }}>
              <ListItemIcon>
                <ImageOutlinedIcon />
              </ListItemIcon>
              Delete Company Logo
            </MenuItem>
          }
          {user?.type === USER_TYPES.TPN_ADMIN && <MenuItem onClick={() => setShowEditCompanyLegalContactModal(true)}>
            <ListItemIcon>
              <DescriptionIcon />
            </ListItemIcon>
            Edit Legal Contact
          </MenuItem>}
        </Menu>
      </>}
    </>
  );
}

function VendorProfileV2() {
  const {
    user,
  } = useAppSelector(state => state.user);
  const [company, setCompany] = useState<Company | undefined>(undefined);

  useEffect(() => {
    if (!user || user.companies == undefined) return;
    if (user.companies?.length < 1) return;
    // CompanyProfile renders its header/tabs from the lightweight /company-detail/
    // endpoints and only loads the full company lazily, so pass through what we have.
    setCompany(user.companies[0]);
  }, [user]);

  return (
    <Layout fullBleed pageTitle={`${company ? company.name : 'Company'} Profile`}>
      {company && <CompanyProfileV2 company={company} /> }
    </Layout>
  );
}

export function CompanyProfile(props: { company: Company; routed?: boolean }) {
  const version = useProfileVersion();
  const { routed, ...v1Props } = props;
  return version.showV2 ? <CompanyProfileV2 {...props} /> : <CompanyProfileV1 {...v1Props} />;
}

export default function VendorProfile() {
  const version = useProfileVersion();
  return version.showV2 ? <VendorProfileV2 /> : <VendorProfileV1 />;
}
